import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, Paper, Grid,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, MenuItem, Checkbox, FormControlLabel
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { contractService, contractTypeService } from '../services/api';
import ContractUpload from './ContractUpload';
import ContractUploadForPerson from './ContractUploadForPerson';
import { employeeService } from '../services/api';
import { positionService, seniorityLevelService } from '../services/api';
import ErrorDialog from './Common/ErrorDialog';
import { usePermissions } from './PermissionsContext';
import { useConfirm } from './Common/ConfirmDialog';
import { useToast } from './Common/ToastProvider';


const ContractForm = ({ embedded=false, contractId=null, personId=null, disabled: disabledProp=false, onSaved, onCancel }) => {
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const routeId = id;
  const { role } = usePermissions();

  // HRManager can only view, not edit (read-only mode)
  const isHRManagerReadOnly = role === 'HRManager';
  const disabled = disabledProp || isHRManagerReadOnly;

  // Track the actual contract ID (may differ from prop if we detect existing contract)
  const [detectedContractId, setDetectedContractId] = useState(null);
  const effectiveId = embedded ? (contractId || detectedContractId) : routeId;
  const isEdit = Boolean(effectiveId);

  const [formData, setFormData] = useState({
    contractNumber: '',
    contractDate: '',
    fullName: '',
    personalNumber: '',
    jobTitleName: '',
    jobTitleCode: '',
    workplace: '',
    contractTerm: '',
    contractTypeId: '',
    probationPeriodMonths: 0,
    startDate: '',
    baseSalary: 0,
    salaryCurrency: 'EUR',   // S3
    salaryBasis: 'Gross',    // S3
    annualLeaveDaysPerYear: 20,
    contractFilePath: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(false);
  const [positions, setPositions] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [employeeFormData, setEmployeeFormData] = useState({
    person: { firstName: '', lastName: '' },
    personalIdNumber: '',
    email: '',
    positionId: '',
    seniorityLevelId: '',
    // HireDate removed - now derived from Contract.StartDate
    status: 'Active'
  });

  const [showUpload, setShowUpload] = useState(false);

  // Pending file for upload on Save (Вариант 3 - upload happens on Save, not on Extract)
  const [pendingFile, setPendingFile] = useState(null);

  // Duplicate contracts detection
  const [duplicateContracts, setDuplicateContracts] = useState([]);
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [deleteWithFile, setDeleteWithFile] = useState(true);

  // Error dialog state
  const [errorDialog, setErrorDialog] = useState({ open: false, title: '', message: '', severity: 'error' });

  const showError = (message, title = 'Error') => {
    setErrorDialog({ open: true, title, message, severity: 'error' });
  };

  const showWarning = (message, title = 'Warning') => {
    setErrorDialog({ open: true, title, message, severity: 'warning' });
  };

  // Check if a contract already exists for this person (to handle edge case of re-extraction)
  useEffect(() => {
    const checkExistingContracts = async () => {
      if (embedded && personId) {
        try {
          // Get ALL contracts for this person to detect duplicates
          const contracts = await contractService.getAllByPersonId(personId);
          if (contracts && contracts.length > 1) {
            // Duplicates detected!
            setDuplicateContracts(contracts);
            setShowDuplicatesDialog(true);
          } else if (contracts && contracts.length === 1 && !contractId) {
            // Single contract exists - use it
            setDetectedContractId(contracts[0].id);
          }
        } catch (err) {
          // 404 or empty means no contracts - this is fine
          if (err.response?.status !== 404) {
            console.error('Error checking for existing contracts:', err);
          }
        }
      }
    };
    checkExistingContracts();
  }, [embedded, personId, contractId]);

  const fetchContract = async () => {
    try {
      const contract = await contractService.getById(effectiveId);
      setFormData({
        ...contract,
        contractDate: contract.contractDate.split('T')[0],
        startDate: contract.startDate.split('T')[0]
      });

      // Fetch employee if exists
      await fetchEmployee(contract.personalNumber);

    } catch (error) {
      setError('Error loading contract');
    }
  };

  const fetchEmployee = async (personalNumber) => {
    setLoadingEmployee(true);
    try {
      const employees = await employeeService.getAll();
      const foundEmployee = employees.find(emp => emp.personalIdNumber === personalNumber);
      setEmployee(foundEmployee || null);

      if (foundEmployee) {
        setEmployeeFormData({
          person: { firstName: foundEmployee.person?.firstName || '', lastName: foundEmployee.person?.lastName || '' },
          personalIdNumber: foundEmployee.personalIdNumber,
          email: foundEmployee.email,
          positionId: foundEmployee.positionId,
          seniorityLevelId: foundEmployee.seniorityLevelId,
          // HireDate removed - now derived from Contract.StartDate
          status: foundEmployee.status
        });
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
    } finally {
      setLoadingEmployee(false);
    }
  };



  const handleCreateEmployee = async () => {
    try {
      await contractService.createEmployeeFromContract(id);
      toast('Employee created successfully!', { variant: 'success' });
      await fetchEmployee(formData.personalNumber);
    } catch (error) {
      toast('Error creating employee: ' + (error.response?.data || error.message), { variant: 'error' });
    }
  };

  // Reset form when contractId changes (e.g., after creating a new contract)
  useEffect(() => {
    if (embedded && contractId) {
      // Contract was just created or changed - fetch it
      fetchContract();
    } else if (embedded && !contractId) {
      // No contract yet - reset to empty form
      setFormData({
        contractNumber: '',
        contractDate: '',
        fullName: '',
        personalNumber: '',
        jobTitleName: '',
        jobTitleCode: '',
        workplace: '',
        contractTerm: '',
        contractTypeId: '',
        probationPeriodMonths: 0,
        startDate: '',
        baseSalary: 0,
        salaryCurrency: 'EUR',   // S3
        salaryBasis: 'Gross',    // S3
        annualLeaveDaysPerYear: 20,
        contractFilePath: ''
      });
    }
  }, [contractId, embedded]);

  useEffect(() => {
    if (isEdit) {
      // Load contract data when in edit mode (including embedded with detected contract)
      fetchContract();
    }
    loadLookupData();
  }, [effectiveId, isEdit]);

  useEffect(() => {
    if (formData.personalNumber && isEdit) {
      fetchEmployee(formData.personalNumber);
    }
  }, [formData.personalNumber, isEdit]);

  const handleChange = (e) => {
    if (disabled) return;
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExtractedData = (extractedContract) => {
    // Remove contractTerm from OpenAI extraction - it should be selected manually
    const { contractTerm, ...cleanExtractedData } = extractedContract;
    setFormData({
      ...cleanExtractedData,
      contractTerm: '', // Keep empty for manual selection
      contractDate: extractedContract.contractDate ? extractedContract.contractDate.split('T')[0] : '',
      startDate: extractedContract.startDate ? extractedContract.startDate.split('T')[0] : ''
    });
  };

  const handleDeleteContract = async () => {
    if (!effectiveId) return;

    const confirmMessage = formData.contractFilePath
      ? 'Are you sure you want to delete this contract? The associated document file will also be deleted.'
      : 'Are you sure you want to delete this contract?';

    if (!(await confirm({ message: confirmMessage, danger: true, confirmLabel: 'Delete' }))) return;

    setLoading(true);
    setError('');

    try {
      await contractService.delete(effectiveId);

      // Reset form state
      setDetectedContractId(null);
      setFormData({
        contractNumber: '',
        contractDate: '',
        fullName: '',
        personalNumber: '',
        jobTitleName: '',
        jobTitleCode: '',
        workplace: '',
        contractTerm: '',
        contractTypeId: '',
        probationPeriodMonths: 0,
        startDate: '',
        baseSalary: 0,
        salaryCurrency: 'EUR',   // S3
        salaryBasis: 'Gross',    // S3
        annualLeaveDaysPerYear: 20,
        contractFilePath: ''
      });
      setPendingFile(null);

      if (!embedded) {
        navigate('/contracts');
      } else {
        // Notify parent to refresh
        onSaved?.({ deleted: true });
      }
    } catch (err) {
      console.error('Error deleting contract:', err);
      showError('Could not delete the contract. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Delete a duplicate contract
  const handleDeleteDuplicate = async (contractToDelete) => {
    setLoading(true);
    try {
      await contractService.delete(contractToDelete.id, deleteWithFile);

      // Refresh duplicates list
      const remaining = duplicateContracts.filter(c => c.id !== contractToDelete.id);
      setDuplicateContracts(remaining);

      if (remaining.length <= 1) {
        // No more duplicates
        setShowDuplicatesDialog(false);
        if (remaining.length === 1) {
          setDetectedContractId(remaining[0].id);
        }
        // Refresh parent
        onSaved?.({ duplicateResolved: true });
      }
    } catch (err) {
      console.error('Error deleting duplicate contract:', err);
      showError('Could not delete the contract. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Select which contract to keep
  const handleKeepContract = (contractToKeep) => {
    setDetectedContractId(contractToKeep.id);
    setShowDuplicatesDialog(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const cleanFormData = { ...formData };

      // Convert contractTypeId: empty string -> null, string -> int
      if (cleanFormData.contractTypeId === '' || cleanFormData.contractTypeId === null || cleanFormData.contractTypeId === undefined) {
        cleanFormData.contractTypeId = null;
      } else if (typeof cleanFormData.contractTypeId === 'string') {
        cleanFormData.contractTypeId = parseInt(cleanFormData.contractTypeId, 10) || null;
      }

      // Ensure numeric fields are proper numbers
      cleanFormData.probationPeriodMonths = parseInt(cleanFormData.probationPeriodMonths, 10) || 0;
      cleanFormData.baseSalary = parseFloat(cleanFormData.baseSalary) || 0;
      cleanFormData.annualLeaveDaysPerYear = parseInt(cleanFormData.annualLeaveDaysPerYear, 10) || 20;

      // Ensure personId is set when creating in embedded mode (directly in payload)
      if (!isEdit && embedded && personId) {
        cleanFormData.personId = personId;
      }

      let savedContractId = effectiveId;

      if (isEdit) {
        await contractService.update(effectiveId, cleanFormData);
        setError(''); // Clear any previous errors
      } else {
        const response = await contractService.create(cleanFormData);
        savedContractId = response.id;
      }

      // Upload pending file to Azure Blob after contract is saved (now we have contractNumber)
      if (pendingFile && personId && cleanFormData.contractNumber) {
        try {
          const uploadResponse = await contractService.uploadFile(
            pendingFile,
            personId,
            cleanFormData.contractNumber,  // Use contractNumber for naming convention
            'Labor_Contract'
          );
          // Update contract with the blob path
          await contractService.update(savedContractId, {
            ...cleanFormData,
            id: savedContractId,
            contractFilePath: uploadResponse.blobPath
          });
          setPendingFile(null); // Clear pending file
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          // Contract is saved, but file upload failed - show warning but don't fail
          showWarning('Contract saved, but file upload failed. Please try uploading again.', 'Upload Warning');
          setLoading(false);
          return;
        }
      }

      // Navigate or notify parent
      if (!embedded) {
        navigate('/contracts');
      } else {
        onSaved?.({ id: savedContractId });
      }

    } catch (error) {
      console.error('Contract submit error:', error);

      // Handle specific error codes from backend
      const errorData = error.response?.data;
      if (errorData?.errorCode === 'DUPLICATE_CONTRACT_NUMBER') {
        showError(`A contract with number "${formData.contractNumber}" already exists. Please use a different contract number.`, 'Duplicate Contract Number');
      } else if (errorData?.errorCode === 'DUPLICATE_PERSON_CONTRACT') {
        showError('This person already has a contract. Please update the existing contract instead of creating a new one.', 'Contract Already Exists');
        // Try to load the existing contract
        if (errorData.existingContractId) {
          setDetectedContractId(errorData.existingContractId);
        }
      } else {
        // Generic error
        showError('Could not save the contract. Please check the form and try again.', 'Save Error');
      }
    } finally {
      setLoading(false);
    }
  };









  const loadLookupData = async () => {
    try {
      // Wait a bit for token to be set
      await new Promise(resolve => setTimeout(resolve, 200));

      const [positions, levels, contractTypes] = await Promise.all([
        positionService.getAll(),
        seniorityLevelService.getAll(),
        contractTypeService.getAll()
      ]);
      setPositions(positions || []);
      setSeniorityLevels(levels || []);
      setContractTypes(contractTypes || []);
    } catch (error) {
      console.error('Error loading lookup data:', error);
      setError('Failed to load lookup data');
      setPositions([]);
      setSeniorityLevels([]);
      setContractTypes([]);
    }
  };

  const handleEmployeeChange = (e) => {
    const { name, value } = e.target;
    if (name === 'firstName' || name === 'lastName') {
      setEmployeeFormData(prev => ({
        ...prev,
        person: { ...(prev.person || {}), [name]: value }
      }));
    } else {
      setEmployeeFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSaveEmployee = async () => {
    try {
      const employeeData = {
        ...employeeFormData,
        firstName: undefined, // deprecated in UI; backend uses employee.person
        lastName: undefined,
        id: employee.id,
        positionId: parseInt(employeeFormData.positionId),
        seniorityLevelId: parseInt(employeeFormData.seniorityLevelId)
        // HireDate removed - now derived from Contract.StartDate
      };

      await employeeService.update(employee.id, employeeData);
      setEditingEmployee(false);
      await fetchEmployee(formData.personalNumber);
      toast('Employee updated successfully!', { variant: 'success' });
    } catch (error) {
      toast('Error updating employee: ' + (error.response?.data || error.message), { variant: 'error' });
    }
  };

  const handleCancelEmployeeEdit = () => {
    setEditingEmployee(false);
    // Reset form data to original employee data
    if (employee) {
      setEmployeeFormData({
        person: { firstName: employee.person?.firstName || '', lastName: employee.person?.lastName || '' },
        personalIdNumber: employee.personalIdNumber,
        email: employee.email,
        positionId: employee.positionId,
        seniorityLevelId: employee.seniorityLevelId,
        // HireDate removed - now derived from Contract.StartDate
        status: employee.status
      });
    }
  };

  const handleEditEmployee = () => {
    if (employee) {
      setEmployeeFormData({
        person: { firstName: employee.person?.firstName || '', lastName: employee.person?.lastName || '' },
        personalIdNumber: employee.personalIdNumber,
        email: employee.email,
        positionId: employee.positionId,
        seniorityLevelId: employee.seniorityLevelId,
        // HireDate removed - now derived from Contract.StartDate
        status: employee.status
      });
    }
    setEditingEmployee(true);
  };

  // Handler for ContractUploadForPerson - receives extracted data AND the file (for upload on Save)
  const handleUploadForPersonExtracted = ({ extractedData, file }) => {
    const ex = extractedData || {};

    // Store file for upload on Save
    if (file) {
      setPendingFile(file);
    }

    setFormData(prev => ({
      ...prev,
      fullName: ex.fullName || prev.fullName,
      personalNumber: ex.personalNumber || prev.personalNumber,
      startDate: ex.startDate ? String(ex.startDate).substring(0, 10) : prev.startDate,
      contractNumber: ex.contractNumber || prev.contractNumber,
      contractDate: ex.contractDate ? String(ex.contractDate).substring(0, 10) : prev.contractDate,
      jobTitleName: ex.jobTitleName || prev.jobTitleName,
      jobTitleCode: ex.jobTitleCode || prev.jobTitleCode,
      probationPeriodMonths: ex.probationPeriodMonths ?? prev.probationPeriodMonths,
      annualLeaveDaysPerYear: ex.annualLeaveDaysPerYear ?? prev.annualLeaveDaysPerYear,
      workplace: ex.workplace || prev.workplace,
      contractTerm: ex.contractTerm || prev.contractTerm,
      contractTypeId: ex.contractTypeId ?? prev.contractTypeId,
      baseSalary: ex.baseSalary ?? prev.baseSalary
    }));
  };

  return (
    <Box>
      {/* Error Dialog */}
      <ErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        title={errorDialog.title}
        message={errorDialog.message}
        severity={errorDialog.severity}
      />

      {/* Duplicate Contracts Dialog */}
      <Dialog open={showDuplicatesDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.light' }}>
          ⚠️ Multiple Contracts Detected
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This person has {duplicateContracts.length} contracts. Only one contract per person is allowed.
            Please select which contract to keep and delete the others.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contract #</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Has File</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {duplicateContracts.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.contractNumber || '—'}</TableCell>
                  <TableCell>{c.fullName || '—'}</TableCell>
                  <TableCell>{c.contractDate ? new Date(c.contractDate).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{c.contractFilePath ? '✓' : '—'}</TableCell>
                  <TableCell>{c.recordCreatedAt ? new Date(c.recordCreatedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => handleKeepContract(c)}
                      sx={{ mr: 1 }}
                    >
                      Keep
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteDuplicate(c)}
                      disabled={loading}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteWithFile}
                onChange={(e) => setDeleteWithFile(e.target.checked)}
              />
            }
            label="Also delete associated document files when deleting contracts"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDuplicatesDialog(false)}>
            Close (Resolve Later)
          </Button>
        </DialogActions>
      </Dialog>

      {/* For embedded mode with personId, use ContractUploadForPerson for AI extraction */}
      {/* File upload happens on Save (Вариант 3) */}
      {embedded && personId && (
        <ContractUploadForPerson
          personId={personId}
          onExtracted={handleUploadForPersonExtracted}
          existingBlobPath={formData.contractFilePath}
          disabled={disabled}
        />
      )}

      {/* Show indicator if file is pending upload */}
      {pendingFile && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            📎 File "{pendingFile.name}" will be uploaded when you save the contract.
          </Typography>
        </Box>
      )}

      {/* For non-embedded or no personId, show the old Upload & Apply dialog */}
      {(!embedded || !personId) && (
        <>
          <Button size="small" variant="outlined" onClick={()=> setShowUpload(true)} disabled={disabled}>Upload & Apply</Button>
          <Dialog open={showUpload} onClose={()=> setShowUpload(false)} fullWidth maxWidth="xl">
            <DialogTitle>Upload contract and apply data</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display:'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(520px, 52vw) 1fr' }, gap: 2 }}>
                <ContractUpload compact previewHeight={560} onPreviewUrlReady={(url)=>{ setFormData(prev=> ({ ...prev, contractFilePath: url })); }} onEmployeeExtracted={(ex)=>{
                  setFormData(prev=> ({ ...prev,
                    contractFilePath: prev.contractFilePath,
                    fullName: ex.fullName || prev.fullName,
                    personalNumber: ex.personalNumber || prev.personalNumber,
                    startDate: ex.startDate ? String(ex.startDate).substring(0,10) : prev.startDate,
                    contractNumber: ex.contractNumber || prev.contractNumber,
                    contractDate: ex.contractDate ? String(ex.contractDate).substring(0,10) : prev.contractDate,
                    jobTitleName: ex.jobTitleName || prev.jobTitleName,
                    jobTitleCode: ex.jobTitleCode || prev.jobTitleCode,
                    probationPeriodMonths: ex.probationPeriodMonths ?? prev.probationPeriodMonths,
                    annualLeaveDaysPerYear: ex.annualLeaveDaysPerYear ?? prev.annualLeaveDaysPerYear,
                    workplace: ex.workplace || prev.workplace,
                    contractTerm: ex.contractTerm || prev.contractTerm,
                    contractTypeId: (ex.contractTypeId ?? prev.contractTypeId),
                    baseSalary: (ex.baseSalary ?? prev.baseSalary)
                  }));
                }} />
                <Paper sx={{ p:2, maxHeight: 600, overflow: 'auto' }}>
                  <Typography variant="subtitle2" sx={{ mb:1 }}>Current form values</Typography>
                  <Typography variant="body2">Contract Number: {formData.contractNumber||'—'}</Typography>
                  <Typography variant="body2">Contract Date: {formData.contractDate||'—'}</Typography>
                  <Typography variant="body2">Start Date: {formData.startDate||'—'}</Typography>
                  <Typography variant="body2">Full Name: {formData.fullName||'—'}</Typography>
                  <Typography variant="body2">Personal Number: {formData.personalNumber||'—'}</Typography>
                  <Typography variant="body2">Job Title: {formData.jobTitleName||'—'} ({formData.jobTitleCode||'—'})</Typography>
                  <Typography variant="body2">Workplace: {formData.workplace||'—'}</Typography>
                  <Typography variant="body2">Contract Term: {formData.contractTerm||'—'}</Typography>
                  <Typography variant="body2">Probation (months): {formData.probationPeriodMonths ?? '—'}</Typography>
                  <Typography variant="body2">Base Salary: {formData.baseSalary ?? '—'}</Typography>
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={()=> setShowUpload(false)} disabled={disabled}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {!embedded && (
        <Typography variant="h4" gutterBottom>
          {isEdit ? 'Edit Contract' : 'Add New Contract'}
        </Typography>
      )}

      {!isEdit && !embedded && (
        <ContractUpload onEmployeeExtracted={handleExtractedData} />
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Show loading indicator when fetching contract data */}
      {loading && embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Зареждане на договор...</Typography>
        </Box>
      )}

      <Paper sx={{ p: 3 }}>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contract Number"
                name="contractNumber"
                value={formData.contractNumber}
                onChange={handleChange}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contract Date"
                name="contractDate"
                type="date"
                value={formData.contractDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Personal Number (ЕГН)"
                name="personalNumber"
                value={formData.personalNumber}
                onChange={handleChange}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Job Title Name"
                name="jobTitleName"
                value={formData.jobTitleName}
                onChange={handleChange}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Job Title Code"
                name="jobTitleCode"
                value={formData.jobTitleCode}
                onChange={handleChange}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Workplace"
                name="workplace"
                value={formData.workplace}
                onChange={handleChange}
                required
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contract Term"
                name="contractTerm"
                value={formData.contractTerm}
                onChange={handleChange}
                placeholder="безсрочен / срочен ..."
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Contract Type"
                name="contractTypeId"
                value={formData.contractTypeId}
                onChange={handleChange}
                disabled={disabled}
              >
                {contractTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField disabled={disabled}
                fullWidth
                label="Probation Period (Months)"
                name="probationPeriodMonths"
                type="number"
                value={formData.probationPeriodMonths}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled={disabled}
                fullWidth
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField disabled={disabled}
                fullWidth
                label="Annual Leave (days per year)"
                name="annualLeaveDaysPerYear"
                type="number"
                value={formData.annualLeaveDaysPerYear}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Base Salary"
                name="baseSalary"
                type="number"
                step="0.01"
                value={formData.baseSalary}
                onChange={handleChange}
                required
                disabled={disabled}
                helperText="As written in the contract"
              />
            </Grid>
            {/* S3 — currency and gross/net exactly as written in the contract */}
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Currency" name="salaryCurrency"
                value={formData.salaryCurrency || 'BGN'} onChange={handleChange} disabled={disabled}>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="BGN">BGN</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Gross / Net" name="salaryBasis"
                value={formData.salaryBasis || 'Gross'} onChange={handleChange} disabled={disabled}>
                <MenuItem value="Gross">Gross</MenuItem>
                <MenuItem value="Net">Net</MenuItem>
              </TextField>
            </Grid>

          </Grid>

          {!disabled && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => { if (!embedded) navigate('/contracts'); else onCancel?.(); }}
                >
                  {isEdit ? 'Close' : 'Cancel'}
                </Button>
              </Box>
              {isEdit && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteContract}
                  disabled={loading}
                >
                  Delete Contract
                </Button>
              )}
            </Box>
          )}
        </form>
      </Paper>

      {/* Employee Section (hidden in embedded mode) */}
      {!embedded && isEdit && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Employee Information
          </Typography>

          {loadingEmployee ? (
            <CircularProgress />
          ) : employee ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={employeeFormData.person?.firstName || ''}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={employeeFormData.person?.lastName || ''}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Personal ID Number"
                    name="personalIdNumber"
                    value={employeeFormData.personalIdNumber}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={employeeFormData.email}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select={editingEmployee}
                    label="Position"
                    name="positionId"
                    value={editingEmployee ? employeeFormData.positionId : (employee.position?.title || 'N/A')}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    InputLabelProps={{ shrink: true }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' },
                      '& .MuiInputLabel-root': { fontSize: '1rem' }
                    }}
                  >
                    {editingEmployee && positions.map((position) => (
                      <MenuItem key={position.id} value={position.id}>
                        {position.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select={editingEmployee}
                    label="Seniority Level"
                    name="seniorityLevelId"
                    value={editingEmployee ? employeeFormData.seniorityLevelId : (employee.seniorityLevel?.name || 'N/A')}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    InputLabelProps={{ shrink: true }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' },
                      '& .MuiInputLabel-root': { fontSize: '1rem' }
                    }}
                  >
                    {editingEmployee && seniorityLevels.map((level) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hire Date"
                    name="hireDate"
                    type={editingEmployee ? "date" : "text"}
                    value={editingEmployee ? employeeFormData.hireDate : new Date(employee.hireDate).toLocaleDateString()}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    InputLabelProps={editingEmployee ? { shrink: true } : {}}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'background.paper' : 'action.hover' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select={editingEmployee}
                    label="Status"
                    name="status"
                    value={employeeFormData.status}
                    onChange={handleEmployeeChange}
                    InputProps={{ readOnly: !editingEmployee }}
                    variant={editingEmployee ? "outlined" : "filled"}
                    sx={{
                      '& .MuiInputBase-input': { color: 'text.primary' },
                      '& .MuiInputBase-root': { backgroundColor: editingEmployee ? 'white' : '#f8f9fa' }
                    }}
                  >
                    {editingEmployee && [
                      <MenuItem key="Active" value="Active">Active</MenuItem>,
                      <MenuItem key="Inactive" value="Inactive">Inactive</MenuItem>,
                      <MenuItem key="Terminated" value="Terminated">Terminated</MenuItem>,
                      <MenuItem key="OnLeave" value="OnLeave">On Leave</MenuItem>,
                      <MenuItem key="OnMaternityLeave" value="OnMaternityLeave">On Maternity/Paternity Leave</MenuItem>,
                      <MenuItem key="OnSickLeave" value="OnSickLeave">On Sick Leave</MenuItem>,
                      <MenuItem key="Probation" value="Probation">Probation</MenuItem>,
                      <MenuItem key="Suspended" value="Suspended">Suspended</MenuItem>,
                      <MenuItem key="NoticePeriod" value="NoticePeriod">Notice Period</MenuItem>,
                      <MenuItem key="Retired" value="Retired">Retired</MenuItem>
                    ]}
                  </TextField>
                </Grid>
              </Grid>

              {!disabled && (
                editingEmployee ? (
                  <Box display="flex" gap={2} sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveEmployee}
                    >
                      Save Employee
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancelEmployeeEdit}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={handleEditEmployee}
                    sx={{ mt: 2 }}
                  >
                    Edit Employee
                  </Button>
                )
              )}
            </>
          ) : (
            <Box>
              <Typography color="text.secondary" gutterBottom>
                No employee found with personal number: {formData.personalNumber}
              </Typography>
              {!disabled && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCreateEmployee}
                >
                  Create Employee from Contract
                </Button>
              )}
            </Box>
          )}
        </Paper>
      )}




    </Box>
  );
};

export default ContractForm;





















































